'use client';

import { Autocomplete, AutocompleteProps, TextField, FormControl, FormHelperText } from '@mui/material';
import { useState } from 'react';

export interface FormAutocompleteProps<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'> {
  /** The label text to display */
  label?: string;
  /** Whether this field is required (if false/undefined, field is optional) */
  required?: boolean;
  /** Whether the field is in edit mode (required indicator only shows in edit mode) */
  isEditMode?: boolean;
  /** Error state for the input */
  error?: boolean;
  /** Helper text to display */
  helperText?: string;
  /** Size of the input */
  size?: 'small' | 'medium';
  /** Whether the input is disabled */
  disabled?: boolean;
}

/**
 * An Autocomplete wrapper that automatically handles FormLabel integration
 * for consistent required/optional field indicators.
 *
 * Default behavior:
 * - No autoselect on open (autoSelect defaults to false)
 * - Smart autoselect: automatically selects on blur (Enter, Tab, clicking elsewhere) when the list is narrowed to exactly one option
 * - If you need a pre-selected value, set an initial value prop instead of using autoSelect
 *
 * @example
 * // Basic usage with smart autoselect
 * <FormAutocomplete
 *   options={options}
 *   label="Field Name"
 *   getOptionLabel={(option) => option.name}
 * />
 *
 * @example
 * // Required field with initial value
 * <FormAutocomplete
 *   options={options}
 *   label="Field Name"
 *   required
 *   value={initialValue}
 *   getOptionLabel={(option) => option.name}
 * />
 *
 */
export const FormAutocomplete = <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  label,
  required,
  isEditMode = true,
  error,
  helperText,
  size = 'medium',
  disabled,
  ...autocompleteProps
}: FormAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) => {
  const isMultiSelect = autocompleteProps.multiple === true;

  // Track input text to shrink label immediately when user starts typing (for search)
  const [inputValue, setInputValue] = useState(autocompleteProps.inputValue || '');

  // For multi-select: use TextField with label that moves to border when values are selected
  // This matches the FormSelect styling
  if (isMultiSelect) {
    const hasValue = Array.isArray(autocompleteProps.value) && autocompleteProps.value.length > 0;

    // Build label string with required/optional indicators to match FormSelect style
    let labelText = label || '';
    if (label && isEditMode) {
      if (required) {
        labelText = `${label} (Required)`;
      } else {
        labelText = `${label} (Optional)`;
      }
    }

    return (
      <FormControl fullWidth error={error} size={size} disabled={disabled}>
        <Autocomplete
          {...autocompleteProps}
          autoSelect={autocompleteProps.autoSelect ?? false}
          disabled={disabled}
          inputValue={autocompleteProps.inputValue !== undefined ? autocompleteProps.inputValue : inputValue}
          onInputChange={(event, newInputValue, reason) => {
            if (autocompleteProps.inputValue === undefined) {
              setInputValue(newInputValue);
            }
            if (autocompleteProps.onInputChange) {
              autocompleteProps.onInputChange(event, newInputValue, reason);
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              margin: 0,
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={labelText}
              size={size}
              fullWidth
              required={false}
              InputLabelProps={{
                shrink: hasValue || inputValue.length > 0,
                required: false,
              }}
            />
          )}
        />
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>
    );
  }

  // Single-select: use Material-UI's built-in label with required prop
  const hasValue =
    autocompleteProps.value !== null && autocompleteProps.value !== undefined && autocompleteProps.value !== '';

  // Build label string with required/optional indicators to match FormSelect style
  let labelText = label || '';
  if (label && isEditMode) {
    if (required) {
      labelText = `${label} (Required)`;
    } else {
      labelText = `${label} (Optional)`;
    }
  }

  // Shrink label when there's a value or when user is typing
  const shouldShrink = hasValue || inputValue.length > 0;

  return (
    <Autocomplete
      {...autocompleteProps}
      autoSelect={autocompleteProps.autoSelect ?? false}
      disabled={disabled}
      inputValue={autocompleteProps.inputValue !== undefined ? autocompleteProps.inputValue : inputValue}
      onInputChange={(event, newInputValue, reason) => {
        // Track input value for label shrinking
        if (autocompleteProps.inputValue === undefined) {
          setInputValue(newInputValue);
        }

        // Smart autoselect: on blur (Enter, Tab, clicking elsewhere) when narrowed to exactly one option
        // This gives users control - they can type, see it narrows to one, then blur to select
        if (
          reason === 'blur' &&
          newInputValue &&
          !autocompleteProps.value &&
          autocompleteProps.options &&
          autocompleteProps.onChange
        ) {
          // Use MUI's filterOptions if provided, otherwise use simple substring matching
          const filterOptions = autocompleteProps.filterOptions;
          let filtered: T[];

          if (filterOptions && typeof filterOptions === 'function') {
            // Use custom filter function - convert readonly array to array for filterOptions
            const optionsArray = Array.isArray(autocompleteProps.options)
              ? [...autocompleteProps.options]
              : Array.from(autocompleteProps.options);
            const getOptionLabel = autocompleteProps.getOptionLabel || ((option: T) => String(option));
            filtered = filterOptions(optionsArray, {
              inputValue: newInputValue,
              getOptionLabel: getOptionLabel as any,
            }) as T[];
          } else {
            // Default: simple case-insensitive substring match
            const getLabel = autocompleteProps.getOptionLabel || ((option: T) => String(option));
            const optionsArray = Array.isArray(autocompleteProps.options)
              ? [...autocompleteProps.options]
              : Array.from(autocompleteProps.options);
            filtered = optionsArray.filter((option) => {
              const label = getLabel(option);
              return label.toLowerCase().includes(newInputValue.toLowerCase());
            });
          }

          if (filtered.length === 1) {
            // Type assertion needed because onChange expects the value type which can be T | T[] | null
            autocompleteProps.onChange(event, filtered[0] as any, 'selectOption', undefined);
          }
        }

        // Call original onInputChange if provided
        if (autocompleteProps.onInputChange) {
          autocompleteProps.onInputChange(event, newInputValue, reason);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={labelText}
          error={error}
          helperText={helperText}
          size={size}
          fullWidth
          InputLabelProps={{
            shrink: shouldShrink,
            required: false, // Don't show Material-UI's asterisk, FormLabel handles it
          }}
        />
      )}
    />
  );
};

export default FormAutocomplete;
